import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { useCart } from '../context/CartContext';

const NAV_LINKS = [
  { label: 'Home',     path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'Shop',     path: '/shop' },
  { label: 'About',    path: '/about' },
  { label: 'Contact',  path: '/contact' },
];

const SCROLL_THRESHOLD = 20;

// Header follows each page's hero theme when at top (same shade as hero)
const HERO_THEMES: Record<string, 'primary' | 'primaryOverlay' | 'light'> = {
  '/': 'primary',
  '/services': 'primaryOverlay',
  '/about': 'primaryOverlay',
  '/contact': 'primaryOverlay',
  '/shop': 'light',
  '/blog': 'light',
};

const Navbar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [atTop, setAtTop] = useState(true);
  const { pathname } = useLocation();
  const { count: cartCount } = useCart();

  const themeKey = pathname.startsWith('/blog') ? '/blog' : pathname;
  const heroTheme = HERO_THEMES[themeKey];
  const atTopWithHero = atTop && heroTheme;
  const isPrimaryHero = atTopWithHero === 'primary' || atTopWithHero === 'primaryOverlay';
  const isPrimaryOverlay = atTopWithHero === 'primaryOverlay'; // same shade as hero overlay (primary/90)
  const isLightHero = atTopWithHero === 'light';

  useEffect(() => {
    const onScroll = () => setAtTop(window.scrollY < SCROLL_THRESHOLD);
    setAtTop(window.scrollY < SCROLL_THRESHOLD);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // In a real app, this would route to a search results page or filter context
      // For now, we simulate a working search by going to the shop page 
      // where products can be viewed.
      window.location.href = `/shop?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  const navBg = isPrimaryHero
    ? (isPrimaryOverlay ? 'bg-primary/90 shadow-none' : 'bg-primary shadow-none')
    : isLightHero ? 'bg-[#FAF8F5] shadow-none md:bg-[#FAF8F5] md:shadow-none' : 'bg-white shadow-md';
  const logoWhite = isPrimaryHero;
  const logoSize = atTopWithHero ? 'h-11 md:h-12' : 'h-14 md:h-[4.25rem]';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
      <div className="container-main flex items-center justify-between h-20 py-3">
        {/* Logo from manlab.co.zw (works on Vercel without committing the file) */}
        <Link to="/" className="flex items-center gap-3 shrink-0">
          <img 
            src="/images/home/Manlab-Medicals-Incorporated.png"
            alt="Manlab Medicals" 
            className={`w-auto object-contain transition-all duration-300 ${logoSize} ${
              logoWhite ? 'brightness-0 invert' : ''
            } ${!atTopWithHero ? 'drop-shadow-sm' : ''}`}
          />
        </Link>

        {/* Desktop Navigation - hidden when search is open */}
        <ul className={`hidden md:flex items-center gap-1 mx-4 transition-opacity duration-200 ${searchOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
          {NAV_LINKS.map(link => (
            <li key={link.path}>
              <Link
                to={link.path}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                  isPrimaryHero
                    ? (pathname === link.path ? 'bg-white/20 text-white' : 'text-white/90 hover:text-white hover:bg-white/10')
                    : isLightHero
                      ? (pathname === link.path ? 'bg-primary/10 text-primary' : 'text-gray-700 hover:text-primary hover:bg-gray-200/50')
                      : pathname === link.path
                        ? 'bg-primary/10 text-primary drop-shadow-sm'
                        : 'text-gray-600 hover:text-primary hover:bg-gray-100/50'
                }`}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>

        {/* Utility / CTA / Hamburger */}
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          
          {/* Search Icon & Input */}
          <div className="relative flex items-center group">
            <form 
              onSubmit={handleSearch}
              className={`flex items-center transition-all duration-300 overflow-hidden ${
                searchOpen ? 'w-48 sm:w-64 opacity-100 px-3 py-1.5' : 'w-0 opacity-0 p-0'
              } ${isPrimaryHero ? 'bg-white/20 border-white/30' : isLightHero ? 'bg-gray-200/80 border-gray-300' : 'bg-gray-100/80 border-black/5'} border rounded-full mr-1`}
            >
              <SearchIcon fontSize="small" className={isPrimaryHero ? 'text-white/80 mr-2' : 'text-gray-400 mr-2'} />
              <input 
                type="text"
                placeholder="Search products..."
                autoFocus={searchOpen}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`bg-transparent border-none outline-none text-sm w-full focus:ring-0 ${isPrimaryHero ? 'text-white placeholder-white/60' : 'text-gray-800 placeholder-gray-400'}`}
              />
            </form>
            <button 
              onClick={() => setSearchOpen(!searchOpen)}
              className={`p-2 rounded-full transition-colors ${isPrimaryHero ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-primary hover:bg-gray-100/50'}`}
              aria-label="Toggle search"
            >
              {searchOpen ? <CloseIcon fontSize="small" /> : <SearchIcon fontSize="small" />}
            </button>
          </div>

          <Link
            to="/enquiry"
            className={`relative p-2 rounded-full transition-colors ${isPrimaryHero ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-primary hover:bg-gray-100/50'}`}
            aria-label="Enquiry list"
          >
            <ShoppingCartOutlinedIcon fontSize="small" />
            {cartCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-accent text-primary-dark text-[10px] font-bold rounded-full px-1">
                {cartCount > 99 ? '99+' : cartCount}
              </span>
            )}
          </Link>

          <Link to="/contact" className={`hidden lg:inline-flex items-center gap-2 font-bold text-sm rounded-full transition-all duration-300 ${
            isPrimaryHero ? 'bg-white text-primary !py-2.5 !px-5 hover:bg-white/90 shadow-md' : 'btn-primary !py-2.5 !px-5 ring-1 ring-black/5 shadow-md'
          }`}>
            Get A Quote <ArrowForwardIcon fontSize="small" />
          </Link>
          
          <button
            onClick={() => setOpen(!open)}
            className={`md:hidden p-2 rounded-xl transition-colors ${isPrimaryHero ? 'text-white hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100/80'}`}
            aria-label="Toggle menu"
          >
            {open ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - full page solid white overlay */}
      {open && (
        <div className="fixed inset-0 md:hidden flex flex-col" style={{ zIndex: 9999, backgroundColor: '#ffffff' }}>
          {/* Top bar with close */}
          <div className="flex items-center justify-between px-6 h-20 flex-shrink-0">
            <Link to="/" onClick={() => setOpen(false)} className="flex items-center">
              <img 
                src="/images/home/Manlab-Medicals-Incorporated.png"
                alt="Manlab Medicals" 
                className="h-11 w-auto object-contain"
              />
            </Link>
            <button
              onClick={() => setOpen(false)}
              className="p-3 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Close menu"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Links pushed to bottom, left-aligned */}
          <nav className="flex-1 flex flex-col justify-end px-6 pb-6">
            <ul className="flex flex-col gap-1 w-full">
              {NAV_LINKS.map((link, i) => (
                <li key={link.path} className="animate-fade-in" style={{ animationDelay: `${i * 50}ms` }}>
                  <Link
                    to={link.path}
                    onClick={() => setOpen(false)}
                    className={`block py-3 px-4 rounded-2xl text-lg font-semibold transition-all text-left ${
                      pathname === link.path
                        ? 'bg-primary/10 text-primary'
                        : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                    }`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li className="animate-fade-in" style={{ animationDelay: `${NAV_LINKS.length * 50}ms` }}>
                <Link
                  to="/enquiry"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 py-3 px-4 rounded-2xl text-lg font-semibold text-gray-700 hover:text-primary hover:bg-gray-50 text-left w-full"
                >
                  Enquiry list {cartCount > 0 && <span className="bg-accent text-primary-dark text-xs font-bold rounded-full min-w-[20px] h-6 flex items-center justify-center px-2">{cartCount}</span>}
                </Link>
              </li>
            </ul>

            {/* Get A Quote button at the very bottom */}
            <div className="border-t border-gray-100 pt-5 mt-4 animate-fade-in">
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="btn-primary w-full justify-center py-4 text-base"
              >
                Get A Quote <ArrowForwardIcon fontSize="small" />
              </Link>
            </div>
          </nav>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
